import React, { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import Select from 'react-select';
import AsyncSelect from 'react-select/async';

import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Grid';

import Label from 'components/Label';
import Button from 'components/Button';
import V3Input from 'v3/components/Input';
import api from 'services/api';
import moment from 'moment';

import { validatePermission } from 'actions/index';
import { useEffectThrottled } from 'hooks';
import { fetchCustomer, fetchCustomers } from 'utils/fetches';
import theme from 'v3/theme';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function ClientSelector({
  onClientSelected,
  onListReports,
  pathname,
  buttonName = 'Filtrar Viagens',
  loading: dataLoading,
  hideMainButton = false,
  hideDatePicker,
  hasClient = true,
  hasCostCenter = false,
  hasUser = false,
  hasTags = false,
  ...props
}) {
  const labelStyle = { color: theme.colors.dark, fontWeight: 500 };
  const hasAllCompanies = validatePermission('VISUALIZAR_TODAS_AS_EMPRESAS');

  const navigate = useNavigate();
  const query = useQuery();
  const [clients, setClients] = useState([]);
  const [startDate, setStartDate] = useState(
    moment(query.get('start_date')).isValid()
      ? query.get('start_date')
      : moment().startOf('month').format('YYYY-MM-DD')
  );
  const [endDate, setEndDate] = useState(
    moment(query.get('end_date')).isValid()
      ? query.get('end_date')
      : moment().endOf('month').format('YYYY-MM-DD')
  );
  const [startDateInput, setStartDateInput] = useState(
    moment(query.get('start_date')).isValid()
      ? moment(query.get('start_date')).format('DD/MM/YYYY')
      : moment().startOf('month').format('DD/MM/YYYY')
  );
  const [endDateInput, setEndDateInput] = useState(
    moment(query.get('end_date')).isValid()
      ? moment(query.get('end_date')).format('DD/MM/YYYY')
      : moment().endOf('month').format('DD/MM/YYYY')
  );
  const [selectedCompany, setSelectedCompany] = useState();
  const [selectedUser, setSelectedUser] = useState();
  const [selectedTags, setSelectedTags] = useState();
  const [companies, setCompanies] = useState([]);
  const [checked, setChecked] = useState([]);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.get('companies'))
      setChecked(
        query
          .get('companies')
          .split(',')
          .map(item => Number(item))
      );
  }, [query.get('companies')]);

  useEffect(() => {
    getClients();
    getCompanies();
  }, []);

  useEffect(() => {
    if (clients) {
      setValues(clients.filter(client => clients.includes(client.id)));
    }
  }, [clients]);

  useEffect(() => {
    if (clients.length && checked.length) {
      setValues(clients.filter(client => checked.includes(client.id)));
    }
  }, [checked, clients]);

  useEffect(() => {
    onClientSelected(clients.filter(client => checked.includes(client.id)));
  }, [values]);

  function handleSelectedCostCenters(selected) {

    if (selected) {
      const mapped = selected.map(item => item.id);
      query.set('cost_center_id', mapped.join(','));
    } else {
      query.set('cost_center_id', '');
    }
    setupQueryParams();
  }

  function handleSelect(selected) {
    setValues(selected);

    if (selected) {
      const mapped = selected.map(item => item.id);
      setChecked(mapped);
      query.set('companies', mapped.join(','));
    } else {
      query.set('companies', '');
      setChecked([]);
    }
    setupQueryParams();
  }

  useEffectThrottled(
    () => {
      query.delete('tags');

      if (selectedTags?.length) {
        selectedTags.forEach(tag => {
          query.append('tags', tag.id);
        });

        setupQueryParams();
      }
    },

    [selectedTags],
    1000
  );

  useEffectThrottled(
    () => {
      if (selectedUser?.id) {
        query.set('user_id', selectedUser?.id);

        setupQueryParams();
      }
    },
    [selectedUser?.id],
    1000
  );

  useEffect(() => {
    if (!startDate) query.delete('start_date');
    else query.set('start_date', moment(startDate).format('YYYY-MM-DD'));
    setupQueryParams();
  }, [startDate]);

  useEffect(() => {
    if (!endDate) query.delete('end_date');
    else query.set('end_date', moment(endDate).format('YYYY-MM-DD'));
    setupQueryParams();
  }, [endDate]);

  useEffect(() => {
    if (!selectedCompany || !hasAllCompanies) {
      query.delete('company_id');
    } else {
      query.set('company_id', selectedCompany.id);
    }
    setupQueryParams();
  }, [selectedCompany]);

  function setupQueryParams() {
    navigate({ pathname: props.url, search: query.toString() });
  }

  async function getClients() {
    setLoading(true);
    const usuario = localStorage.getItem('usuario');
    const jsonU = JSON.parse(usuario);
    const response = await api.get(`clients/user/${jsonU.id}`);
    if (response.status === 200) {
      const clientsAux = response.data.clients;
      const clitentsIds = clientsAux.map(cli => cli.id);

      setClients(clientsAux);
      setChecked(clitentsIds);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }

  async function getCompanies() {
    try {
      setLoading(true);
      const response = await api.get('company');
      setCompanies(response.data);
      setSelectedCompany(
        hasAllCompanies
          ? response.data[0]
          : JSON.parse(localStorage.getItem('usuario')).company
      );
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectCompany(selected) {
    setSelectedCompany(selected);
  }

  useEffect(() => {
    if (clients) {
      const mapped = clients.map(item => item.id);
      setChecked(mapped);
      query.set('companies', mapped.join(','));
      setupQueryParams();
    } else {
      query.set('companies', '');
      setChecked([]);
      setupQueryParams();
    }
  }, [clients]);

  useEffect(() => {
    if (selectedUser) {
      query.set('user_id', selectedUser.id);
      setupQueryParams();
    } else {
      query.set('user_id', '');
      setupQueryParams();
    }
  }, [selectedUser]);

  useEffect(() => {
    if (selectedTags?.length) {
      const mapped = selectedTags?.map(item => item.id);
      query.set('tags', mapped?.join(','));
      setupQueryParams();
    } else {
      query.set('tags', '');
      setupQueryParams();
    }
  }, [selectedTags]);

  function validateDate() {
    const startValue = startDate;
    const endValue = endDate;
    const format = 'YYYY-MM-DD';

    if (!moment(startValue, format, true).isValid()) {
      props.setSnackBar({
        display: true,
        type: 'error',
        message: 'Período "de" inválido.',
      });
      return;
    }
    if (!moment(endValue, format, true).isValid()) {
      props.setSnackBar({
        display: true,
        type: 'error',
        message: 'Período "até" inválido.',
      });
      return;
    }
    setupQueryParams();

    onListReports(
      values?.map(v => v.id),
      startDate,
      endDate,
      1
    );
  }

  async function fetchCostCenters(search) {
    try {
      const response = await api.get(`v3/cost-center`, {
        params: { search },
      });

      return response.data;
    } catch (ex) {
      return [];
    }
  }

  async function fetchTags(search) {
    if (search.length >= 2) {
      const response = await api.get('tags', {
        params: { search, not_paginated: true },
      });

      return response.data;
    }

    return [];
  }

  async function fetchUsers(search) {
    if (search.length >= 3) {
      const response = await api.get('user/select', {
        params: { search },
      });

      return response.data;
    }

    return [];
  }

  return (
    <>
      {loading && (
        <Grid spacing={2}>
          <Grid item xs={6}>
            <Skeleton
              variant="rect"
              width="100%"
              height={80}
              style={{ marginBottom: '10px', marginLeft: '10px' }}
            />
          </Grid>
          <Grid item xs={6}>
            <Skeleton
              variant="rect"
              width="100%"
              height={80}
              style={{ marginBottom: '10px', marginLeft: '10px' }}
            />
          </Grid>
        </Grid>
      )}

      {!loading && (
        <div style={{ width: '100%' }}>
          {!hideDatePicker && (
            <div style={{ marginBottom: 16 }}>
              <Label htmlFor="from" className="textLabel" style={labelStyle}>
                Período de / até
              </Label>
              <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                <V3Input
                  type="date"
                  value={startDate || ''}
                  onChange={(e) => {
                    const next = e?.target?.value || '';
                    setStartDate(next);
                    setStartDateInput(next ? moment(next).format('DD/MM/YYYY') : '');
                  }}
                />
                <V3Input
                  type="date"
                  value={endDate || ''}
                  onChange={(e) => {
                    const next = e?.target?.value || '';
                    setEndDate(next);
                    setEndDateInput(next ? moment(next).format('DD/MM/YYYY') : '');
                  }}
                />
              </div>
            </div>
          )}

          {(hasUser || hasTags) && (
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, width: '100%' }}>
              {hasUser && (
                <div style={{ flex: 1, minWidth: 320 }}>
                  <Label className="textLabel" style={labelStyle}>
                    Usuário
                  </Label>
                  <AsyncSelect
                    noOptionsMessage={() => <span>Digite um usuário</span>}
                    placeholder="Selecione"
                    getOptionLabel={option => option.username}
                    getOptionValue={option => option.id}
                    loadOptions={fetchUsers}
                    loadingMessage={() => <span>Carregando...</span>}
                    value={selectedUser}
                    onChange={e => setSelectedUser(e)}
                  />
                </div>
              )}
              {hasTags && (
                <div style={{ flex: 1, minWidth: 320 }}>
                  <Label className="textLabel" style={labelStyle}>
                    Tags
                  </Label>
                  <AsyncSelect
                    isMulti
                    noOptionsMessage={() => <span>Digite uma tag</span>}
                    placeholder="Selecione"
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    loadOptions={fetchTags}
                    loadingMessage={() => <span>Carregando...</span>}
                    value={selectedTags}
                    onChange={e => setSelectedTags(e)}
                  />
                </div>
              )}
            </div>
          )}

          {hasCostCenter && (
            <div style={{ marginBottom: 16, width: '100%' }}>
              <Label className="textLabel" style={labelStyle}>
                Centro de Custo
              </Label>
              <AsyncSelect
                noOptionsMessage={() => <span>Digite um Centro de Custo</span>}
                placeholder="Selecione"
                isMulti
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                loadOptions={fetchCostCenters}
                loadingMessage={() => <span>Carregando...</span>}
                onChange={handleSelectedCostCenters}
              />
            </div>
          )}

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', width: '100%' }}>
            <div style={{ flex: 1, minWidth: 360 }}>
              <Label className="textLabel" style={labelStyle}>
                {clients.length && checked.length === 0
                  ? 'Selecione um Cliente'
                  : 'Clientes Selecionados'}
              </Label>
              <AsyncSelect
                noOptionsMessage={() => <span>Digite um Cliente</span>}
                placeholder="Selecione"
                isMulti
                getOptionLabel={option => `${option.social_name} - ${option.cgccpf}`}
                getOptionValue={option => option.id}
                loadOptions={(name) => fetchCustomers({ search: name })}
                loadingMessage={() => <span>Carregando...</span>}
                onChange={handleSelect}
              />
            </div>

            {!hideMainButton && (
              <Button
                onClick={validateDate}
                loading={(loading, dataLoading)}
                style={{ marginTop: '32px' }}
                disabled={dataLoading}
              >
                {buttonName}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ClientSelector;
